import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { api, hasActiveSubscription } from "../../../services/api";
import {
  connectDomainKeepo,
  disconnectDomainKeepo,
  keepoConnectErrorKey,
  DomainDns,
  DomainOwner,
  getDomainDns,
  OwnedDomain,
} from "../../../services/domains";

export type DomainKeepo = { siteId: string; title: string; url: string; plus: boolean };
type Detail = OwnedDomain & { owner?: Partial<DomainOwner> };

/**
 * The Keepo a domain opens, on its page: the person's Keepos to pick from, connecting, moving and disconnecting
 * (which change the DNS too: it's loaded again after, through `showDns`).
 */
export const useKeepoConnection = ({
  owned,
  setOwned,
  isPending,
  showDns,
  setSaving,
}: {
  owned: Detail;
  setOwned: Dispatch<SetStateAction<Detail | null | undefined>>;
  isPending: boolean;
  showDns: (dns: DomainDns) => void;
  setSaving: (isSaving: boolean) => void;
}) => {
  const { domain } = owned;
  const [keepos, setKeepos] = useState<DomainKeepo[]>([]);
  const [keepoId, setKeepoId] = useState<string | null>(owned.keepo?.siteId ?? null);
  const [isChoosingKeepo, setChoosingKeepo] = useState(false);
  // Right after connecting or disconnecting: the Keepo box loading until the new state is in
  const [isKeepoRefreshing, setKeepoRefreshing] = useState(false);
  const [isDisconnectOpen, setDisconnectOpen] = useState(false);
  const [keepoError, setKeepoError] = useState<string>();

  useEffect(() => {
    if (isPending) {
      return;
    }
    api
      .getSites()
      .then(sites =>
        setKeepos(
          sites.map(item => {
            const data = item.site || item.draft;
            return {
              siteId: item.siteId,
              title: data?.title || "",
              url: data?.url || "",
              plus: hasActiveSubscription(item.subscription),
            };
          })
        )
      )
      .catch(() => undefined);
  }, [domain, isPending]);

  const keepo = keepos.find(item => item.siteId === keepoId) ?? (keepoId ? owned.keepo : null);

  // Connecting (or moving) and disconnecting change the DNS too: it's loaded again after
  const changeKeepo = async (siteId: string | null) => {
    setSaving(true);
    setKeepoError(undefined);
    try {
      const next = siteId ? await connectDomainKeepo(domain, siteId) : await disconnectDomainKeepo(domain);
      // Saved: the modal closes and the Keepo box loads while the DNS (Keepo's records) comes back, then shows it
      setSaving(false);
      setChoosingKeepo(false);
      setKeepoRefreshing(true);
      const dnsNow = await getDomainDns(domain).catch(() => undefined);
      setKeepoId(next.keepo?.siteId ?? null);
      // From what is on the page now: an owner or security change made while the DNS loaded stays
      setOwned(current => current && { ...current, ...next });
      if (dnsNow) {
        showDns(dnsNow);
      }
    } catch (error) {
      setKeepoError(keepoConnectErrorKey(error));
    } finally {
      setSaving(false);
      setKeepoRefreshing(false);
    }
  };

  return {
    keepos,
    keepoId,
    keepo,
    isChoosingKeepo,
    setChoosingKeepo,
    isKeepoRefreshing,
    isDisconnectOpen,
    setDisconnectOpen,
    keepoError,
    setKeepoError,
    changeKeepo,
  };
};
